// src/pages/Admin/Feedbacks/Feedbacks.tsx
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FC, useMemo, useState } from "react";
import { FeedbackEntry, getFeedbacks } from "src/apis/feedback";
import CompanySelect from "src/pages/components/CompanySelect";
import FilterButtons from "src/pages/components/SearchAndResetButtons";
import PageContainer from "src/pages/components/PageContainer";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import { formatDateToBR } from "src/utils/date";
import { isUserAdministrator } from "src/utils/auth";
import { Id } from "src/utils/types";

const Feedbacks: FC = () => {
  const [companyId, setCompanyId] = useState<Id>("");
  const [feedbacks, setFeedbacks] = useState<FeedbackEntry[]>([]);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const hasFeedbacks = useMemo(() => feedbacks.length > 0, [feedbacks]);

  const fetchFeedbacks = async () => {
    setIsLoading(true);
    try {
      const effectiveCompanyId = companyId === "-1" ? undefined : companyId;
      const response = await getFeedbacks(effectiveCompanyId);
      setFeedbacks(response.feedbacks);
      setError("");
    } catch (err: any) {
      setError(err?.message || "Erro ao carregar feedbacks");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer
      title="Feedbacks dos psicólogos"
      error={error}
      guard={isUserAdministrator}
    >
      <Box
        display="flex"
        flexWrap="wrap"
        gap={1}
        alignItems="center"
        justifyContent="center"
      >
        <CompanySelect
          isFilter
          companyId={companyId}
          setCompanyId={setCompanyId}
          setError={setError}
          useAllOption
        />
        <FilterButtons
          isLoading={isLoading}
          search={fetchFeedbacks}
          resetState={() => {
            setCompanyId("");
            setFeedbacks([]);
          }}
        />
      </Box>

      {isLoading && <SkeletonLoad variant="table" rows={6} columns={7} />}

      {!isLoading && hasFeedbacks && (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>ID</strong>
                </TableCell>
                <TableCell>
                  <strong>Empresa</strong>
                </TableCell>
                <TableCell>
                  <strong>Classificação</strong>
                </TableCell>
                <TableCell>
                  <strong>Mensagem</strong>
                </TableCell>
                <TableCell>
                  <strong>Colaborador</strong>
                </TableCell>
                <TableCell>
                  <strong>Anexos</strong>
                </TableCell>
                <TableCell>
                  <strong>Data</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feedbacks.map((feedback) => (
                <TableRow key={feedback.id}>
                  <TableCell>{feedback.id}</TableCell>
            
                  {/* EMPRESA: mostra nome, cai pro id se não tiver */}
                  <TableCell>
                    {feedback.company_name || feedback.company_id}
                  </TableCell>
            
                  <TableCell>
                    <Chip
                      label={feedback.classification}
                      color={
                        feedback.classification === "Situação de risco"
                          ? "error"
                          : "default"
                      }
                    />
                  </TableCell>
            
                  <TableCell>{feedback.message}</TableCell>
            
                  {/* COLABORADOR: nome > id > '-' */}
                  <TableCell>
                    {feedback.collaborator_name ||
                      feedback.collaborator_id ||
                      "-"}
                  </TableCell>
            
                  <TableCell>
                    {feedback.attachments?.length ? (
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        {feedback.attachments.map((attachment, index) => (
                          <a
                            key={`${feedback.id}-att-${index}`}
                            href={attachment.value}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {attachment.value}
                          </a>
                        ))}
                      </Box>
                    ) : (
                      "-"
                    )}
                  </TableCell>
            
                  <TableCell>{formatDateToBR(feedback.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isLoading && !hasFeedbacks && (
        <Typography textAlign="center">
          Nenhum feedback encontrado.
        </Typography>
      )}
    </PageContainer>
  );
};

export default Feedbacks;
